// Typed fetch wrappers for the Signal backend.
// Routes go through nginx (or vite dev proxy) — same-origin, cookie auth.

import type {
  AIAuditResponse,
  ConversionFunnelResult,
  ConversationRollup,
  DataBundle,
  DocumentEvidenceResult,
  HealthSignalsBulkResponse,
  LostCoachingOut,
  LostPatternMatchesResponse,
  MonthlyRevenueResponse,
  MultiDealCoachingOut,
  NextActionOut,
  OpportunityDetailFull,
  PocCatalogRow,
  PocDraftMessage,
  PocRecommendationsOut,
  PipelinePacingReportData,
  PocScanResult,
  RevenueExclusionListResponse,
  RevenueExclusionRow,
  RevenuePacingResponse,
  RevenueProjectionDeltaResponse,
  RevenueProjectionOpportunitiesResponse,
  RevenueProjectionResponse,
  SimilarOppsResponse,
  StageVelocityResult,
  ThreadsOut,
  WeeklyDeltas,
  WhoAmI,
} from "./types";

/**
 * Typed fetch error — carries the HTTP status code so callers can
 * branch on `e.status === 404` instead of regex-matching the message.
 *
 * Pre-PR code-review HIGH (SGNL-230): the prior `new Error(...)` shape
 * forced callers like _loadOppMonthly to do `msg.includes("404")`,
 * which mis-fires on URL paths containing "404" and silently breaks
 * if the error template changes.
 */
export class ApiError extends Error {
  status: number;
  path: string;
  constructor(path: string, status: number) {
    super(`${path} returned ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.path = path;
    // PR-Agent #147 (importance 6): `instanceof ApiError` would
    // otherwise fail on transpile targets that drop the subclass
    // prototype during `super()`. Explicitly re-pin the prototype
    // so the 404 branch in revenue_calendar._loadOppMonthly is
    // reliable across all build configurations.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

async function getJSON<T>(path: string): Promise<T> {
  const r = await fetch(path, { credentials: "same-origin" });
  if (!r.ok) {
    throw new ApiError(path, r.status);
  }
  return (await r.json()) as T;
}

export async function fetchWhoAmI(): Promise<WhoAmI | null> {
  const r = await fetch("/api/whoami", { credentials: "same-origin" });
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`/api/whoami returned ${r.status}`);
  return (await r.json()) as WhoAmI;
}

export async function fetchData(opts: { includePreQual?: boolean } = {}): Promise<DataBundle> {
  // SGNL-228 — `includePreQual=true` brings back the pre-qualification
  // cohort (MQL / Pre-Qual / Stage 0 — probability < 5%). Default
  // behaviour excludes them so KPIs reflect qualifying+ pipeline only.
  const qs = opts.includePreQual ? "?include_pre_qual=true" : "";
  return getJSON<DataBundle>(`/api/data${qs}`);
}

// SGNL-134 — admin AI audit aggregate. Admin-gated server-side; on 403
// the caller surfaces the error so non-admin operators learn they need
// the BOOTSTRAP_ADMINS allowlist.
export async function fetchAIAudit(): Promise<AIAuditResponse> {
  return getJSON<AIAuditResponse>("/api/admin/ai-audit");
}

// SGNL-177 — AI narrative summary of the progress delta view.
export async function fetchProgressSummary(opts?: {
  fromDate?: string;
  toDate?: string;
  daysBack?: number;
}): Promise<{ summary: string; model: string; from_date: string | null; to_date: string | null }> {
  const { fromDate, toDate, daysBack = 7 } = opts ?? {};
  // Only use date-range mode when BOTH dates are provided; a partial range
  // would silently fall back to days_back and return a misleading summary.
  if (fromDate && toDate) {
    const qs = new URLSearchParams({ from: fromDate, to: toDate });
    return getJSON(`/api/opportunities/progress-summary?${qs}`);
  }
  return getJSON(`/api/opportunities/progress-summary?days_back=${daysBack}`);
}

export async function fetchWeeklyDeltas(opts?: {
  daysBack?: number;
  fromDate?: string;
  toDate?: string;
}): Promise<WeeklyDeltas> {
  // SGNL-81 — when fromDate + toDate are both set, use the date-range
  // path (?from=…&to=…). Otherwise fall back to legacy days_back.
  // Partial input (only one date set) is ignored on the frontend; the
  // backend would reject it as a 400 if we forwarded it, and there's
  // no UI state where only one of the two pickers is populated.
  if (opts?.fromDate && opts.toDate) {
    const qs = new URLSearchParams({ from: opts.fromDate, to: opts.toDate });
    return getJSON<WeeklyDeltas>(`/api/opportunities/weekly-deltas?${qs}`);
  }
  const daysBack = opts?.daysBack ?? 7;
  return getJSON<WeeklyDeltas>(`/api/opportunities/weekly-deltas?days_back=${daysBack}`);
}

// SGNL-84 — opp detail page payload (header + summary + tags + conversation arrays).
export async function fetchOpportunityDetail(
  oppId: string,
): Promise<OpportunityDetailFull> {
  return getJSON<OpportunityDetailFull>(
    `/api/opportunities/${encodeURIComponent(oppId)}/detail`,
  );
}

// SGNL-220 — LLM coaching insight for a closed-lost opp. Returns null
// when the opp isn't Closed Lost OR the LLM emitted all-empty output.
// FE renders an empty-state in both cases.
export async function fetchLostCoaching(
  oppId: string,
): Promise<LostCoachingOut | null> {
  return getJSON<LostCoachingOut | null>(
    `/api/opportunities/${encodeURIComponent(oppId)}/lost-coaching`,
  );
}

// SGNL-221 — surface other deals (open + closed-lost) sharing the
// focal Closed Lost opp's stage-path + activity-gap fingerprint.
// Used by the lost-coaching card on opp_detail to render an
// "Other deals at risk" section.
export async function fetchLostPatternMatches(
  oppId: string,
): Promise<LostPatternMatchesResponse> {
  return getJSON<LostPatternMatchesResponse>(
    `/api/opportunities/${encodeURIComponent(oppId)}/lost-pattern-matches`,
  );
}

// SGNL-220 — cross-deal synthesis across the operator's multi-deal
// selection. Returns null when no opp in the request has a cached
// per-deal coaching row to synthesise from.
export async function fetchLostCoachingBatch(
  oppIds: readonly string[],
): Promise<MultiDealCoachingOut | null> {
  const resp = await fetch("/api/opportunities/lost-coaching/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ opportunity_ids: oppIds }),
  });
  if (!resp.ok) {
    throw new Error(`fetchLostCoachingBatch: HTTP ${resp.status}`);
  }
  return (await resp.json()) as MultiDealCoachingOut | null;
}

// SGNL-101 — executive-summary rollup of the opp's conversation
// timeline. Returns null when the opp has zero source rows; the FE
// renders an empty-state placeholder in that case.
export async function fetchConversationRollup(
  oppId: string,
): Promise<ConversationRollup | null> {
  return getJSON<ConversationRollup | null>(
    `/api/opportunities/${encodeURIComponent(oppId)}/conversation-rollup`,
  );
}

// SGNL-144 (Epic A PR-2): top-N opps most similar to this one. Server
// pre-ranks with `sort_by=won_value` so wins-by-amount surface first,
// closed-lost-by-amount second. The closer ANN match is the
// tie-breaker.
export async function fetchSimilarOpps(
  oppId: string,
  opts: { n?: number; sortBy?: "distance" | "won_value" } = {},
): Promise<SimilarOppsResponse> {
  const n = opts.n ?? 5;
  const sortBy = opts.sortBy ?? "won_value";
  return getJSON<SimilarOppsResponse>(
    `/api/opportunities/${encodeURIComponent(oppId)}/similar` +
      `?n=${n}&sort_by=${sortBy}`,
  );
}

// SGNL-146: ranked POC matches for an opp. The endpoint returns the
// cached row on second-and-later visits; the first visit (or any visit
// after a POC catalog edit / new classifier summary) lazily triggers
// the compute on the server. `null` is returned (HTTP 200) when the
// opp exists but has no summary yet — the FE renders an empty-state
// card. The `Refresh` button uses fetchRecommendedPocsRefresh() to
// force a recompute.
export async function fetchRecommendedPocs(
  oppId: string,
): Promise<PocRecommendationsOut | null> {
  return getJSON<PocRecommendationsOut | null>(
    `/api/opportunities/${encodeURIComponent(oppId)}/recommended-pocs`,
  );
}

export async function fetchRecommendedPocsRefresh(
  oppId: string,
): Promise<PocRecommendationsOut | null> {
  const r = await fetch(
    `/api/opportunities/${encodeURIComponent(oppId)}/recommended-pocs/refresh`,
    {
      method: "POST",
      credentials: "same-origin",
    },
  );
  if (!r.ok) {
    throw new Error(`POST /recommended-pocs/refresh returned ${r.status}`);
  }
  return (await r.json()) as PocRecommendationsOut | null;
}

// SGNL-153 — Bulk health-signal fetch for Deal Explorer badge column.
// Accepts up to 100 opp_ids (server-enforced cap). Unknown ids are
// silently dropped from the response; valid ids that have no signals
// return active_signals=[] (clean opp, no badges).
export async function fetchHealthSignals(
  oppIds: string[],
): Promise<HealthSignalsBulkResponse> {
  // PR-Agent inline suggestion on PR #85: guard empty array so we don't
  // issue a request with ?opp_ids= (server rejects Query(min_length=1)).
  if (!oppIds.length) return { signals: {} };
  return getJSON<HealthSignalsBulkResponse>(
    `/api/opportunities/health-signals?opp_ids=${oppIds.map(encodeURIComponent).join(",")}`,
  );
}

// SGNL-152 — Suggested next move card. Every call fires a fresh Sonnet
// call on the server (no cache at v0), so there is no separate "refresh"
// endpoint — just re-calling this function is the refresh path.
// Returns null (HTTP 200) when the opp has no summary yet (empty-state).
// Raises on network or server error (FE renders error+retry state).
export async function fetchNextAction(
  oppId: string,
): Promise<NextActionOut | null> {
  return getJSON<NextActionOut | null>(
    `/api/opportunities/${encodeURIComponent(oppId)}/next-action`,
  );
}

// SGNL-149: Draft Message CTA — Sonnet writes a tailored pitch the AE
// pastes into Slack/email. Returns null (HTTP 200) when the opp has
// no classifier summary yet (nothing to ground the draft against);
// the FE renders an empty-state inside the modal in that case. Throws
// on non-2xx so the modal can surface the error state.
// SGNL-150 — POC Explorer.
export async function fetchPocCatalog(): Promise<PocCatalogRow[]> {
  return getJSON<PocCatalogRow[]>("/api/pocs");
}

// SGNL-150 — runs the incremental scan. May take 30-60s on a cold-cache
// first scan (LLM evaluates the RAG top-100). Subsequent scans are
// usually <5s. FE shows a progress indicator + lets the AsyncIterable
// pattern be replaced by SSE later if streaming becomes useful.
export async function scanPoc(pocId: string): Promise<PocScanResult> {
  const r = await fetch(`/api/pocs/${encodeURIComponent(pocId)}/scan`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!r.ok) {
    throw new Error(`POST /api/pocs/${pocId}/scan returned ${r.status}`);
  }
  return (await r.json()) as PocScanResult;
}

export async function fetchPocDraftMessage(
  oppId: string,
  pocId: string,
): Promise<PocDraftMessage | null> {
  const r = await fetch(
    `/api/opportunities/${encodeURIComponent(oppId)}/pocs/${encodeURIComponent(pocId)}/draft-message`,
    {
      method: "POST",
      credentials: "same-origin",
    },
  );
  if (!r.ok) {
    throw new Error(`POST /pocs/${pocId}/draft-message returned ${r.status}`);
  }
  return (await r.json()) as PocDraftMessage | null;
}

// SGNL-156 — Pipeline analytics endpoints (SGNL-68/69 BE)

export async function fetchStageVelocity(params: {
  from?: string;
  to?: string;
  grouping?: "week" | "month";
}): Promise<StageVelocityResult> {
  const q = new URLSearchParams();
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  if (params.grouping) q.set("grouping", params.grouping);
  return getJSON<StageVelocityResult>(`/api/analytics/stage-velocity?${q}`);
}

export async function fetchConversionFunnel(params: {
  cohort_month_from?: string;
  cohort_month_to?: string;
}): Promise<ConversionFunnelResult> {
  const q = new URLSearchParams();
  if (params.cohort_month_from) q.set("cohort_month_from", params.cohort_month_from);
  if (params.cohort_month_to) q.set("cohort_month_to", params.cohort_month_to);
  return getJSON<ConversionFunnelResult>(`/api/analytics/conversion-funnel?${q}`);
}

// SGNL-27 — Document Evidence: per-attachment tag verdicts + conflict analysis.
// Throws on 404 (opp not found) — callers on the opp detail page are safe
// because fetchOpportunityDetail already confirmed the opp exists.
export async function fetchDocumentEvidence(
  oppId: string,
): Promise<DocumentEvidenceResult> {
  return getJSON<DocumentEvidenceResult>(
    `/api/opportunities/${encodeURIComponent(oppId)}/document-evidence`,
  );
}

// SGNL-72 — Sentiment trajectory: conversation threads with sentiment scores.
export async function fetchThreads(
  oppId: string,
  opts: { force?: boolean } = {},
): Promise<ThreadsOut> {
  const params = new URLSearchParams();
  if (opts.force) params.set("force", "true");
  const qs = params.toString() ? `?${params.toString()}` : "";
  return getJSON<ThreadsOut>(
    `/api/opportunities/${encodeURIComponent(oppId)}/threads${qs}`,
  );
}

// ---------------------------------------------------------------------------
// SGNL-230 — Revenue Calendar
//
// The BE owns three endpoints under /api/revenue-projection. The FE renames
// this surface to "Revenue Calendar" to disambiguate from SGNL-213's existing
// probability-weighted "Revenue Projection" sub-view inside Pipeline Analytics
// (see docs/sgnl-229-revenue-projection.md). The URL prefix stays as the BE
// chose it; only the FE label changes.
// ---------------------------------------------------------------------------

/**
 * Headline projection KPIs: ITB (Closed Won, summed as-is) + per-threshold
 * mutually-exclusive probability-weighted buckets. Empty thresholds → ITB only.
 *
 * Buckets are exclusive ranges sorted descending — selecting both [80, 90]
 * does NOT double-count the 90% deals; they live only in the 90 bucket.
 */
export async function fetchRevenueProjection(opts: {
  year?: number;
  thresholds?: readonly number[];
  /** When false, the BE returns unfiltered totals (matches the
   * legacy pre-SGNL-245 behavior). Default true matches the new
   * Pacing Summary so the Revenue Calendar's top + bottom KPIs agree. */
  excludePassThrough?: boolean;
}): Promise<RevenueProjectionResponse> {
  const params = new URLSearchParams();
  if (opts.year !== undefined) params.set("year", String(opts.year));
  if (opts.thresholds && opts.thresholds.length > 0) {
    params.set("thresholds", opts.thresholds.join(","));
  }
  // Always serialize when explicitly provided, not only when false —
  // keeps the wire payload faithful to what the caller passed and
  // means a future BE default flip can't silently shift behaviour.
  if (opts.excludePassThrough !== undefined) {
    params.set("exclude_pass_through", String(opts.excludePassThrough));
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  return getJSON<RevenueProjectionResponse>(`/api/revenue-projection${qs}`);
}

/**
 * 12-month schedule for a single opportunity, padded with zeros for months
 * with no schedule row. Drives the opp-drill chart.
 *
 * Returns 404 if the opp doesn't exist OR is out of the caller's BU scope
 * (the BE's `rbac.assert_opp_in_scope` returns 404-not-403 to prevent
 * enumeration — SGNL-182 ARCH-182-05).
 */
export async function fetchOpportunityMonthlyRevenue(
  oppId: string,
  opts: { year?: number } = {},
): Promise<MonthlyRevenueResponse> {
  const params = new URLSearchParams();
  if (opts.year !== undefined) params.set("year", String(opts.year));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return getJSON<MonthlyRevenueResponse>(
    `/api/opportunities/${encodeURIComponent(oppId)}/monthly-revenue${qs}`,
  );
}

/**
 * Per-month + per-opp delta between two snapshot dates. ITB delta is always
 * 0 by definition (signed contracts are immutable). Date span is capped at
 * 366 days server-side.
 */
export async function fetchRevenueProjectionDelta(opts: {
  fromDate: string; // YYYY-MM-DD
  toDate: string; // YYYY-MM-DD
  year?: number;
  thresholds?: readonly number[];
  excludePassThrough?: boolean;
}): Promise<RevenueProjectionDeltaResponse> {
  const params = new URLSearchParams();
  params.set("from", opts.fromDate);
  params.set("to", opts.toDate);
  if (opts.year !== undefined) params.set("year", String(opts.year));
  if (opts.thresholds && opts.thresholds.length > 0) {
    params.set("thresholds", opts.thresholds.join(","));
  }
  // Always serialize when explicitly provided, not only when false —
  // keeps the wire payload faithful to what the caller passed and
  // means a future BE default flip can't silently shift behaviour.
  if (opts.excludePassThrough !== undefined) {
    params.set("exclude_pass_through", String(opts.excludePassThrough));
  }
  return getJSON<RevenueProjectionDeltaResponse>(
    `/api/revenue-projection/delta?${params.toString()}`,
  );
}

/**
 * SGNL-233 — list opportunities contributing to a BU's projection for
 * a year. Drives the Revenue Calendar BU drill modal (replaced the v1
 * opportunity-ID lookup form). Order is server-defined:
 * weighted_year_total descending.
 *
 * Pass `businessUnit: null` (or omit) to list opps with no BU set.
 */
export async function fetchRevenueProjectionOpportunities(opts: {
  year: number;
  businessUnit: string | null;
  excludePassThrough?: boolean;
}): Promise<RevenueProjectionOpportunitiesResponse> {
  const params = new URLSearchParams();
  params.set("year", String(opts.year));
  if (opts.businessUnit != null) params.set("bu", opts.businessUnit);
  // Always serialize when explicitly provided, not only when false —
  // keeps the wire payload faithful to what the caller passed and
  // means a future BE default flip can't silently shift behaviour.
  if (opts.excludePassThrough !== undefined) {
    params.set("exclude_pass_through", String(opts.excludePassThrough));
  }
  return getJSON<RevenueProjectionOpportunitiesResponse>(
    `/api/revenue-projection/opportunities?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------------
// SGNL-246 — Revenue Pacing (3-bucket model) + admin exclusion CRUD
// ---------------------------------------------------------------------------

// Types live at the top of the file's imports — re-importing here
// is a no-op for the bundler but keeps the SGNL-246 additions
// visually grouped.

/**
 * 3-bucket pacing KPIs for the year. Replaces the threshold-chip
 * projection — ITB (>=99%), Qualified factored (40-98%), Unqualified
 * (1-39%) — plus derived Revenue Visibility, Go-Get, Pipeline
 * Coverage. Filters out admin-managed pass-through revenue
 * categories (External Licenses/Fees etc.).
 */
export async function fetchRevenuePacing(opts: {
  year?: number;
  budget?: number;
  bus?: readonly string[];
}): Promise<RevenuePacingResponse> {
  const params = new URLSearchParams();
  if (opts.year !== undefined) params.set("year", String(opts.year));
  if (opts.budget !== undefined) params.set("budget", String(opts.budget));
  if (opts.bus && opts.bus.length > 0) params.set("bus", opts.bus.join(","));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return getJSON<RevenuePacingResponse>(`/api/revenue-pacing${qs}`);
}

/** Admin: list the Revenue:RecordType values currently filtered out. */
export async function fetchRevenueExclusions(): Promise<RevenueExclusionListResponse> {
  return getJSON<RevenueExclusionListResponse>("/api/admin/revenue-exclusions");
}

/** Admin: add a new RecordType to the exclusion list. Idempotent — re-POSTing
 * an existing category returns the existing row unchanged (200). */
export async function createRevenueExclusion(
  category: string,
  reason: string | null = null,
): Promise<RevenueExclusionRow> {
  const resp = await fetch("/api/admin/revenue-exclusions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ category, reason }),
  });
  if (!resp.ok) {
    throw new ApiError(
      `createRevenueExclusion: HTTP ${resp.status}`,
      resp.status,
    );
  }
  return (await resp.json()) as RevenueExclusionRow;
}

/** Admin: remove a RecordType from the exclusion list. Returns 204. */
export async function deleteRevenueExclusion(category: string): Promise<void> {
  // Encode the path component — categories can contain '/' (e.g.
  // "External Licenses/Fees"). The route uses {category:path} to
  // round-trip the encoded slash safely.
  const encoded = encodeURIComponent(category);
  await fetch(`/api/admin/revenue-exclusions/${encoded}`, {
    method: "DELETE",
    credentials: "include",
  }).then((r) => {
    if (r.status !== 204) {
      throw new ApiError(`DELETE failed: HTTP ${r.status}`, r.status);
    }
  });
}


// ---------------------------------------------------------------------------
// SGNL-247 — Report runner (generic /api/reports/{id}/run wrapper)
// ---------------------------------------------------------------------------

/**
 * Generic run-result envelope returned by `POST /api/reports/{id}/run`.
 *
 * The BE returns:
 *   - `report_id`: stable kebab-case id matching the catalog
 *   - `headline`: 1-line subject-line-grade summary (or null)
 *   - `data`: report-specific JSON payload — shape varies per report
 *   - `html`: prerendered email body, same content as the inline preview
 *
 * `T` defaults to `unknown` so the callsite picks the typed payload
 * (e.g. `fetchReportRun<PipelinePacingReportData>(...)`).
 */
export interface ReportRunResponse<T = unknown> {
  report_id: string;
  headline: string | null;
  data: T;
  html: string;
}

export async function fetchReportRun<T = unknown>(
  reportId: string,
  params: Record<string, unknown> = {},
): Promise<ReportRunResponse<T>> {
  const resp = await fetch(`/api/reports/${encodeURIComponent(reportId)}/run`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ params }),
  });
  if (!resp.ok) {
    throw new ApiError(
      `fetchReportRun(${reportId}): HTTP ${resp.status}`,
      resp.status,
    );
  }
  return (await resp.json()) as ReportRunResponse<T>;
}

/** Typed convenience wrapper for the Pipeline Pacing Dashboard report. */
export async function fetchPipelinePacingReport(opts: {
  buScope?: readonly string[];
  budget?: number;
}): Promise<ReportRunResponse<PipelinePacingReportData>> {
  const params: Record<string, unknown> = {};
  if (opts.buScope && opts.buScope.length > 0) {
    params.bu_scope = [...opts.buScope];
  }
  // Send the budget whenever provided (including 0). The FE filters
  // 0 → undefined at the input layer; this wrapper just faithfully
  // serialises whatever the caller passed so the BE never silently
  // drops a value the FE thinks it sent.
  if (opts.budget !== undefined) {
    params.budget = opts.budget;
  }
  return fetchReportRun<PipelinePacingReportData>("pipeline-pacing", params);
}
